import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Box, Chip } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import WayfinderMainCategoriesTemplate from "./WayfinderMainCategoriesTemplate";
import UseToast from "../UseToast";
import useS3Upload from "../../hooks/useS3Upload";
import { usePxToVw } from "../../hooks/usePxToVw";

const WayfinderMainCategory = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const { userId } = useParams();
  const { showErrorToast, showSuccessToast } = UseToast();
  const navigate = useNavigate();
  const {pxToVw} = usePxToVw();

  // S3 Upload hook
  const { uploadFile, uploading } = useS3Upload({
    onSuccess: (fileUrl) => {
      showSuccessToast("Logo uploaded successfully!");
    },
    onError: (error) => {
      showErrorToast("Failed to upload logo");
      console.error("Upload error:", error);
    },
  });

  // Initial state with only 4 corner logos
  const [categoryData, setCategoryData] = useState({
    userId: "",
    logos: {
      upperLeft: {
        logoUrl: "",
        logoFileName: "",
        enabled: false,
        left: 10,
        top: 10,
        width: 100,
        height: 100,
      },
      upperRight: {
        logoUrl: "",
        logoFileName: "",
        enabled: false,
        left: 70,
        top: 10,
        width: 100,
        height: 100,
      },
      bottomLeft: {
        logoUrl: "",
        logoFileName: "",
        enabled: false,
        left: 10,
        top: 80,
        width: 100,
        height: 100,
      },
      bottomRight: {
        logoUrl: "",
        logoFileName: "",
        enabled: false,
        left: 70,
        top: 80,
        width: 100,
        height: 100,
      },
    },
    status: true,
    header: {
      text: "",
      color: "#000000",
      fontSize: 24,
      fontWeight: 600,
      textAlign: "center",
      opacity: 1,
      textShadowH: "2",
      textShadowV: "2",
      textShadowBlur: "4",
      textShadowColor: "#000000",
      top: 10,
      left: 10,
    },
    description: {
      text: "",
      color: "#000000",
      fontSize: 16,
      fontWeight: 400,
      textAlign: "left",
      opacity: 1,
      textShadowH: "0",
      textShadowV: "0",
      textShadowBlur: "0",
      textShadowColor: "#000000",
      top: 60,
      left: 10,
    },
    footer: {
      text: "",
      color: "#000000",
      fontSize: 14,
      fontWeight: 400,
      textAlign: "center",
      opacity: 0.8,
      textShadowH: "0",
      textShadowV: "0",
      textShadowBlur: "0",
      textShadowColor: "#000000",
      top: 250,
      left: 10,
    },
    searchConfig: {
      enabled: true,
      placeholder: "Search...",
      backgroundColor: "#ffffff",
      textColor: "#000000",
      borderColor: "#cccccc",
      borderRadius: "4px",
      borderWidth: "1px",
      boxShadow: "",
      height: "40px",
      padding: "0 10px",
    },
  });

  useEffect(() => {
    fetchCategoryData();
  }, []);

  const fetchCategoryData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/wayfinder/main-category/${userId}`
      );
      if (response.data.success) {
        const data = response.data.data;
        setCategoryData(data);
      }
    } catch (error) {
      console.error("Error fetching category data:", error);
      showErrorToast(
        error.response?.data?.message || "Failed to load category data"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setCategoryData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTextStylingChange = (section, data) => {
    setCategoryData((prev) => ({
      ...prev,
      [section]: data,
    }));
  };

  // Handle logo property changes
  const handleLogoChange = (position, field, value) => {
    setCategoryData((prev) => ({
      ...prev,
      logos: {
        ...prev.logos,
        [position]: {
          ...prev.logos[position],
          [field]: value,
        },
      },
    }));
  };

  // Upload logo to S3 and update state
  const handleUploadLogo = async (position, file) => {
    if (!file) {
      showErrorToast("Please select a file first");
      return;
    }

    try {
      setLoading(true);

      // Upload to S3
      const fileUrl = await uploadFile(file);

      // Update category data with new logo URL
      setCategoryData((prev) => ({
        ...prev,
        logos: {
          ...prev.logos,
          [position]: {
            ...prev.logos[position],
            logoUrl: fileUrl,
            logoFileName: file.name,
            enabled: true,
          },
        },
      }));
    } catch (error) {
      console.error("Error uploading logo:", error);
      showErrorToast("Failed to upload logo");
    } finally {
      setLoading(false);
    }
  };

  // Delete logo
  const handleDeleteLogo = async (position) => {
    try {
      setCategoryData((prev) => ({
        ...prev,
        logos: {
          ...prev.logos,
          [position]: {
            ...prev.logos[position],
            logoUrl: "",
            logoFileName: "",
            enabled: false,
          },
        },
      }));
      showSuccessToast("Logo removed successfully");
    } catch (error) {
      showErrorToast("Failed to remove logo");
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `/api/wayfinder/save-main-category/${userId}`,
        categoryData
      );
      if (response.data.success) {
        showSuccessToast("Category settings saved successfully!");
      } else {
        showErrorToast(response.data.message || "Failed to save");
      }
    } catch (error) {
      console.error("Error saving category data:", error);
      showErrorToast("Failed to save category settings");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `/api/wayfinder/reset-main-category/${userId}`
      );
      if (response.data.success) {
        setCategoryData(response.data.data);
        showSuccessToast("Settings reset successfully");
      }
    } catch (error) {
      showErrorToast("Failed to reset settings");
    } finally {
      setLoading(false);
    }
  };

  const getPositionName = (position) => {
    const names = {
      upperLeft: "Upper Left",
      upperRight: "Upper Right",
      bottomLeft: "Bottom Left",
      bottomRight: "Bottom Right",
    };
    return names[position] || position;
  };

  const renderLivePreview = () => {
    const textShadowStyle = (data) =>
      `${data.textShadowH}px ${data.textShadowV}px ${data.textShadowBlur}px ${data.textShadowColor}`;

    return (
      <Box
        sx={{
          position: "relative",
          height: "1920px",
          width: '70vw',
          display: "flex",
          alignSelf: "center",
          backgroundColor: "#f5f7fa",
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          overflow: "hidden",
          p: 2,
        }}
      >
        {/* Corner Logos */}
        {Object.entries(categoryData.logos || {}).map(
          ([position, logo]) =>
            logo?.logoUrl &&
            logo?.enabled && (
              <Box
                key={position}
                component="img"
                src={logo.logoUrl}
                alt={getPositionName(position)}
                sx={{
                  position: "absolute",
                  left: `${logo.left}%`,
                  top: `${logo.top}%`,
                  width: pxToVw(`${logo.width}`),
                  height: pxToVw(`${logo.height}`),
                  objectFit: "contain",
                  opacity: categoryData.status ? 1 : 0.5,
                  filter: categoryData.status ? "none" : "grayscale(100%)",
                  transition: "all 0.3s ease",
                  zIndex: 99,
                }}
              />
            )
        )}

        {/* Header Preview */}
        {categoryData.header.text && (
          <Box
            sx={{
              position: "absolute",
              left: `${categoryData.header.left}%`,
              top: `${categoryData.header.top}%`,
              color: categoryData.header.color,
              fontSize: pxToVw(`${categoryData.header.fontSize || '2'}`),
              fontWeight: categoryData.header.fontWeight,
              textAlign: categoryData.header.textAlign,
              opacity: categoryData.header.opacity,
              textShadow: textShadowStyle(categoryData.header),
              width: "100%",
              zIndex: 10,
            }}
          >
            {categoryData.header.text}
          </Box>
        )}

        {/* Description Preview */}
        {categoryData.description.text && (
          <Box
            sx={{
              position: "absolute",
              left: `${categoryData.description.left}%`,
              top: `${categoryData.description.top}%`,
              color: categoryData.description.color,
              fontSize: pxToVw(`${categoryData.description.fontSize || '2'}`),
              fontWeight: categoryData.description.fontWeight,
              textAlign: categoryData.description.textAlign,
              opacity: categoryData.description.opacity,
              textShadow: textShadowStyle(categoryData.description),
              width: "100%",
              zIndex: 10,
            }}
          >
            {categoryData.description.text}
          </Box>
        )}

        {/* Footer Preview */}
        {categoryData.footer.text && (
          <Box
            sx={{
              position: "absolute",
              left: `${categoryData.footer.left}%`,
              top: `${categoryData.footer.top}%`,
              color: categoryData.footer.color,
              fontSize: pxToVw(`${categoryData.footer.fontSize || '2'}`),
              fontWeight: categoryData.footer.fontWeight,
              textAlign: categoryData.footer.textAlign,
              opacity: categoryData.footer.opacity,
              textShadow: textShadowStyle(categoryData.footer),
              zIndex: 10,
              width: "100%",
            }}
          >
            {categoryData.footer.text}
          </Box>
        )}

        {/* Search Preview */}
        {categoryData.searchConfig.enabled && (
          <Box
            sx={{
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              width: "80%",
              maxWidth: 400,
              zIndex: 10,
            }}
          >
            <input
              type="text"
              placeholder={categoryData.searchConfig.placeholder}
              style={{
                width: "100%",
                height: categoryData.searchConfig.height,
                padding: categoryData.searchConfig.padding,
                backgroundColor: categoryData.searchConfig.backgroundColor,
                color: categoryData.searchConfig.textColor,
                borderWidth: categoryData.searchConfig.borderWidth,
                borderStyle: "solid",
                borderColor: categoryData.searchConfig.borderColor,
                borderRadius: categoryData.searchConfig.borderRadius,
                fontSize: "14px",
                boxShadow: categoryData.searchConfig.boxShadow,
                outline: "none",
              }}
            />
          </Box>
        )}

        {/* Status Indicator */}
        <Chip
          icon={categoryData.status ? <Visibility /> : <VisibilityOff />}
          label={categoryData.status ? "Active" : "Inactive"}
          color={categoryData.status ? "success" : "default"}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, zIndex: 10 }}
        />
      </Box>
    );
  };

  return (
    <>
      <WayfinderMainCategoriesTemplate
        title="Main Category"
        categoryData={categoryData}
        setCategoryData={setCategoryData}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        loading={loading || uploading}
        setLoading={setLoading}
        message={message}
        setMessage={setMessage}
        fetchInitialData={fetchCategoryData}
        handleSave={handleSave}
        handleReset={handleReset}
        renderLivePreview={renderLivePreview}
        handleTextStylingChange={handleTextStylingChange}
        handleUploadLogo={handleUploadLogo}
        handleFieldChange={handleFieldChange}
        handleLogoChange={handleLogoChange}
        handleDeleteLogo={handleDeleteLogo}
        getPositionName={getPositionName}
      />
    </>
  );
};

export default WayfinderMainCategory;
